import datapoints from '../datapoints';
import { addDays, compare, getTimestamp, isSameDay, OwaDate, startOfDay } from 'owa-datetime';
import { AgendaItem, AgendaItemType } from 'mini-agenda';
import { CalendarEvent } from 'mini-calendar-event-types';
import { getCalendarEventsCountForADay } from 'mini-calendar-events-store';
import { SUFFIX_AGENDA_SEPARATOR, SUFFIX_AGENGA_EVENT } from './agendaConstants';
import { AgendaRenderPriority } from 'mini-agenda/lib/schema/AgendaItem';

/**
 * Converts list of calendar events to list of agenda items for the distinct days
 * @param calendarItems Calendar events to be converted to agenda items
 * @param distinctDays List of distinct days
 */
export default function convertCalendarEventsToAgendaItems(
    calendarItemsToConvert: CalendarEvent[],
    startDay: OwaDate,
    endDay: OwaDate,
    appContext?: any
): AgendaItem[] {
    const agendaItems: AgendaItem[] = [];

    const agendaEventMap: { [key: number]: number } = {};
    const eventCountMap: { [key: number]: number } = {};

    if (startDay && endDay && calendarItemsToConvert) {
        let eventIndex = 0;
        let currentDistinctDay = startDay;

        // Currently when we update the time, and return back then the the calendar items returned from DAL are not in sorted time.
        // We need to sort the calendar items before we convert them in agenda items
        const calendarItems = calendarItemsToConvert.sort((calItem1, calItem2) =>
            compare(calItem1.Start, calItem2.Start)
        );

        // Loop till distinct days are present or events are present
        while (compare(currentDistinctDay, endDay) <= 0 || eventIndex < calendarItems.length) {
            const currentEvent = calendarItems[eventIndex];

            // Check if event belongs to current day
            // If not, this means we have crossed the boundary between days
            const isDayBoundaryCrossed =
                !currentEvent || !isSameDay(currentEvent.Start, currentDistinctDay);

            // The day card header or 'no event' item needs to be added when:
            // 1. The day boundary is crossed or
            // 2. When events are no longer present so we need to fill the grid with day header and no event card till distinct days are present
            if (isDayBoundaryCrossed) {
                // Now we can safely increment the dayIndex as dayBoundary got crossed
                currentDistinctDay = addDays(currentDistinctDay, 1);

                // Add the day header if the distinct day is still present post dayIndex increment
                // The header is never generated for the first day
                if (currentDistinctDay <= endDay) {
                    const agendaItemTime = getTimestamp(currentDistinctDay);
                    const eventCountForDay = getCalendarEventsCountForADay(currentDistinctDay);

                    const eventCountKey = getTimestamp(startOfDay(currentDistinctDay));
                    eventCountMap[eventCountKey] = eventCountForDay;

                    agendaItems.push({
                        type: AgendaItemType.DayMonthSeparator,
                        id: agendaItemTime + SUFFIX_AGENDA_SEPARATOR,
                        timestamp: agendaItemTime,
                        eventCount: eventCountForDay,
                        priority: AgendaRenderPriority.NORMAL,
                    });
                }
            } else {
                // Add the event card
                agendaItems.push({
                    type: AgendaItemType.Event,
                    event: currentEvent,
                    id:
                        currentEvent.ItemId.Id +
                        getTimestamp(currentEvent.Start) +
                        SUFFIX_AGENGA_EVENT,
                    timestamp: getTimestamp(currentEvent.Start),
                    isActive: false,
                    priority: AgendaRenderPriority.NORMAL,
                });

                const agendaEventMapKey = getTimestamp(startOfDay(currentEvent.Start));
                agendaEventMap[agendaEventMapKey] ? agendaEventMap[agendaEventMapKey]++ : 1;

                eventIndex++;
            }
        }
    }

    // The below code can be removed once we figure out the issue of some days rendered empty.
    // Need to log this to identify scenarios of inconsistency between count index and B+ tree index.
    const eventKeys = Object.keys(eventCountMap);
    let countOfInconsistency = 0;
    const inconsistentDates = [];
    eventKeys &&
        eventKeys.forEach((key: string) => {
            if (eventCountMap[key] !== 0 && agendaEventMap[key] === 0) {
                // Separator was added and count is present here but calendar events are not present for this day
                countOfInconsistency++;
                inconsistentDates.push(key);
            }
        });

    appContext &&
        appContext.signal(datapoints.AGENDA_VIEW_STORE.AGENDA_ITEM_DAY_EVENT_COUNT_INCONSISTENCY, {
            countOfInconsistency: countOfInconsistency,
            dates: inconsistentDates,
        });

    return agendaItems;
}
